/**
 * ScheduleSourcePicker — Sprint 4.7.3
 *
 * Modal de busca para escolher um device "fonte" cuja agenda será
 * copiada para o device alvo (o que está sendo configurado).
 *
 * Fluxo:
 *   1. Operador abre via "📋 Copiar de outro device" no ScheduleTab.
 *   2. Vê lista paginada de devices da frota com filtros simples
 *      (busca textual, status, customer, group).
 *   3. Clica num device → carrega snapshot.schedule via API.
 *   4. Modal mostra resumo (enabled, entries, exceptions, último run).
 *   5. Botão "Usar esta agenda" → callback retorna o schedule completo
 *      ao caller, que faz setData(...) no editor local.
 *
 * Operador AINDA precisa clicar "Aplicar" depois — esta sprint só
 * preenche o editor; backend não recebe nada até o submit normal.
 *
 * Frontend-only. Reusa endpoints existentes:
 *   - listDevicesFiltered(filters) — para popular a lista
 *   - getDeviceConfigSnapshot(id)  — para puxar o schedule da fonte
 *   - listScheduleEvents(id, ...)  — para mostrar "último run"
 */
import { useEffect, useMemo, useState } from "react";
import {
  getDeviceConfigSnapshot,
  listDevicesFiltered,
  listScheduleEvents,
} from "../../lib/api.js";
import { deviceStatus, formatRelative, shortDeviceId } from "../../lib/format.js";
import { t } from "../../i18n/index.js";

const STATUS_FILTERS = ["all", "online", "degraded", "offline"];

// ─────────────────────────────────────────────────────────────────────────
// Subcomponente: detail box (resumo da agenda da fonte selecionada)
// ─────────────────────────────────────────────────────────────────────────
function SourceDetailBox({
  device,
  schedule,
  lastEvent,
  loading,
  err,
  onUse,
  excludedSelf,
}) {
  if (!device) {
    return (
      <div className="p-4 text-center text-noc-muted/60 text-xs italic">
        {t("scheduleSource.pickFromList")}
      </div>
    );
  }

  if (excludedSelf) {
    return (
      <div className="p-3 text-xs text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded">
        {t("scheduleSource.selfNotAllowed")}
      </div>
    );
  }

  if (loading) {
    return <div className="p-4 text-xs text-noc-muted">{t("common.loading")}</div>;
  }

  if (err) {
    return (
      <div className="p-3 text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded">
        {err}
      </div>
    );
  }

  const hasSchedule = !!schedule;
  const enabled = hasSchedule && schedule.enabled === true;
  const entries = hasSchedule && Array.isArray(schedule.entries) ? schedule.entries : [];
  const exceptions = hasSchedule && Array.isArray(schedule.exceptions) ? schedule.exceptions : [];
  const isEmpty = entries.length === 0 && exceptions.length === 0;

  return (
    <div className="space-y-3 text-xs">
      {/* Header fonte */}
      <div className="bg-noc-bg/40 border border-noc-border/60 rounded p-2 space-y-1">
        <div className="font-mono text-noc-accent font-semibold">
          {device.device_id}
        </div>
        {device.customer_code && (
          <div className="text-[10px] text-noc-muted">
            {t("devicesList.colCustomer")}: {device.customer_code}
          </div>
        )}
        <div className="text-[10px] text-noc-muted/70">
          {device.fw_version
            ? `firmware ${device.fw_version}`
            : t("scheduleSource.noFwVersion")}
        </div>
      </div>

      {/* Aviso quando NÃO há snapshot (device nunca enviou heartbeat) */}
      {!hasSchedule && (
        <div className="text-[11px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
          ⚠ {t("scheduleSource.noSnapshot")}
        </div>
      )}

      {/* Resumo da agenda */}
      {hasSchedule && (
        <div className="space-y-1.5 bg-noc-bg/30 border border-noc-accent/30 rounded p-2">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-noc-accent">
            {t("scheduleSource.summaryTitle")}
          </div>
          <div className="grid grid-cols-2 gap-y-1 text-[11px]">
            <span className="text-noc-muted">
              {t("scheduleSource.enabledField")}:
            </span>
            <span
              className={
                enabled ? "text-noc-ok font-semibold" : "text-noc-muted"
              }
            >
              {enabled ? t("scheduleSource.enabledYes") : t("scheduleSource.enabledNo")}
            </span>

            <span className="text-noc-muted">
              {t("scheduleSource.entriesField")}:
            </span>
            <span className="font-mono">{entries.length}</span>

            <span className="text-noc-muted">
              {t("scheduleSource.exceptionsField")}:
            </span>
            <span className="font-mono">{exceptions.length}</span>

            <span className="text-noc-muted">
              {t("scheduleSource.lastEventField")}:
            </span>
            <span className="font-mono text-[10px]">
              {lastEvent
                ? `${formatRelative(lastEvent.executed_at || lastEvent.created_at)}`
                : "—"}
            </span>
          </div>

          {/* Aviso quando agenda está vazia (mas tem snapshot) */}
          {isEmpty && (
            <div className="text-[10px] text-noc-warn italic mt-1.5">
              ⚠ {t("scheduleSource.emptyWarning")}
            </div>
          )}
        </div>
      )}

      {/* Botão "Usar esta agenda" */}
      <button
        type="button"
        onClick={onUse}
        disabled={!hasSchedule}
        className="w-full px-3 py-2 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {t("scheduleSource.useThisSchedule")}
      </button>
      <div className="text-[10px] text-noc-muted/70 text-center">
        {t("scheduleSource.useThisHelp")}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────
export default function ScheduleSourcePicker({
  open,
  onClose,
  onPick,             // callback(scheduleObject) — chamado ao confirmar
  excludeDeviceId,    // device alvo (não pode selecionar a si mesmo)
}) {
  // Filtros de busca
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Resultados da lista
  const [devices, setDevices] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listErr, setListErr] = useState(null);

  // Device selecionado + sua agenda
  const [picked, setPicked] = useState(null);
  const [pickedSchedule, setPickedSchedule] = useState(null);
  const [lastEvent, setLastEvent] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailErr, setDetailErr] = useState(null);

  // Reseta estado quando reabre
  useEffect(() => {
    if (open) {
      setPicked(null);
      setPickedSchedule(null);
      setLastEvent(null);
      setDetailErr(null);
    }
  }, [open]);

  // Carrega devices quando abre ou filtros mudam (debounce simples)
  useEffect(() => {
    if (!open) return;
    const ctl = new AbortController();
    const tid = setTimeout(async () => {
      setLoadingList(true);
      setListErr(null);
      try {
        const filters = { limit: 50 };
        if (query.trim()) filters.search = query.trim();
        if (statusFilter !== "all") filters.status = statusFilter;
        const data = await listDevicesFiltered(filters, { signal: ctl.signal });
        setDevices(Array.isArray(data?.devices) ? data.devices : []);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setListErr(e?.message || t("scheduleSource.errLoadList"));
      } finally {
        setLoadingList(false);
      }
    }, 250);
    return () => {
      clearTimeout(tid);
      ctl.abort();
    };
  }, [open, query, statusFilter]);

  // Filtrar localmente para excluir o próprio device alvo
  const visibleDevices = useMemo(() => {
    if (!excludeDeviceId) return devices;
    return devices.filter((d) => d.device_id !== excludeDeviceId);
  }, [devices, excludeDeviceId]);

  // Quando seleciona um device, busca snapshot.schedule + último event
  const selectDevice = async (device) => {
    setPicked(device);
    setPickedSchedule(null);
    setLastEvent(null);
    setDetailErr(null);
    if (!device || device.device_id === excludeDeviceId) return;

    setLoadingDetail(true);
    try {
      // Busca snapshot.schedule (S2.9 fix: a API devolve last_config_snapshot,
      // não snapshot — a chave errada deixava pickedSchedule sempre null).
      const snap = await getDeviceConfigSnapshot(device.device_id);
      const schedule = snap?.last_config_snapshot?.schedule || null;
      setPickedSchedule(schedule);

      // Busca último schedule_event (best-effort — pode falhar se o
      // device estiver em firmware < 2.7.3, devolvemos null silenciosamente)
      try {
        const ev = await listScheduleEvents(device.device_id, { limit: 1 });
        if (ev && Array.isArray(ev.events) && ev.events.length > 0) {
          setLastEvent(ev.events[0]);
        }
      } catch (e) {
        // Ignora — endpoint pode não existir para fw antigo
        if (e?.name !== "AbortError") {
          console.debug("[ScheduleSourcePicker] schedule-events fail:", e?.message);
        }
      }
    } catch (e) {
      setDetailErr(e?.message || t("scheduleSource.errLoadSnapshot"));
    } finally {
      setLoadingDetail(false);
    }
  };

  // Confirma e devolve a agenda
  const confirmPick = () => {
    if (!pickedSchedule) return;
    // Clona profundamente para evitar acoplamento com estado interno
    const out = {
      enabled: pickedSchedule.enabled === true,
      entries: Array.isArray(pickedSchedule.entries)
        ? pickedSchedule.entries.map((e) => ({ ...e }))
        : [],
      exceptions: Array.isArray(pickedSchedule.exceptions)
        ? [...pickedSchedule.exceptions]
        : [],
    };
    onPick?.(out, picked);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2150] bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-3xl flex flex-col"
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
          <div>
            <h3 className="text-sm font-semibold text-noc-accent">
              📋 {t("scheduleSource.title")}
            </h3>
            <div className="text-[10px] text-noc-muted mt-0.5">
              {t("scheduleSource.subtitle")}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-noc-muted hover:text-noc-text text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Body em 2 colunas: lista | detalhes */}
        <div className="flex-1 overflow-hidden flex">
          {/* Coluna esquerda: filtros + lista */}
          <div
            className="flex flex-col border-r border-noc-border"
            style={{ width: "60%", minWidth: 0 }}
          >
            {/* Filtros */}
            <div className="p-3 border-b border-noc-border space-y-2">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("scheduleSource.searchPlaceholder")}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs"
              />
              <div className="flex gap-1">
                {STATUS_FILTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    className={`px-2 py-0.5 text-[10px] rounded border ${
                      statusFilter === s
                        ? "border-noc-accent/30 bg-noc-accent/10 text-noc-accent"
                        : "border-noc-border text-noc-muted hover:text-noc-text"
                    }`}
                  >
                    {t(`devicesList.${s}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Lista de devices */}
            <div className="flex-1 overflow-y-auto p-1">
              {loadingList && devices.length === 0 ? (
                <div className="text-xs text-noc-muted p-3">
                  {t("common.loading")}
                </div>
              ) : listErr ? (
                <div className="text-xs text-noc-err p-3">{listErr}</div>
              ) : visibleDevices.length === 0 ? (
                <div className="text-xs text-noc-muted italic p-3">
                  {t("devicesList.noMatch")}
                </div>
              ) : (
                <div className="space-y-1">
                  {visibleDevices.map((d) => {
                    const isSelected = picked?.device_id === d.device_id;
                    const status = deviceStatus(d);
                    return (
                      <button
                        key={d.device_id}
                        type="button"
                        onClick={() => selectDevice(d)}
                        className={`w-full text-left p-2 rounded border text-xs transition-colors ${
                          isSelected
                            ? "border-noc-accent/30 bg-noc-accent/10"
                            : "border-noc-border/60 bg-noc-bg/30 hover:border-noc-accent/30 hover:bg-noc-bg/50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono font-semibold text-noc-accent truncate">
                            {shortDeviceId(d.device_id)}
                          </span>
                          <StatusDot status={status} />
                        </div>
                        {d.customer_code && (
                          <div className="text-[10px] text-noc-muted truncate mt-0.5">
                            {d.customer_code}
                          </div>
                        )}
                        <div className="text-[9px] text-noc-muted/60 mt-0.5">
                          {d.last_seen
                            ? formatRelative(d.last_seen)
                            : t("scheduleSource.neverSeen")}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Coluna direita: detalhes da agenda fonte */}
          <div className="flex-1 overflow-y-auto p-3" style={{ minWidth: 0 }}>
            <SourceDetailBox
              device={picked}
              schedule={pickedSchedule}
              lastEvent={lastEvent}
              loading={loadingDetail}
              err={detailErr}
              onUse={confirmPick}
              excludedSelf={
                picked?.device_id && picked.device_id === excludeDeviceId
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-noc-border flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text"
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// StatusDot — bolinha colorida por status
// ─────────────────────────────────────────────────────────────────────────
function StatusDot({ status }) {
  const color =
    status === "online"
      ? "bg-noc-ok/10"
      : status === "degraded"
      ? "bg-noc-warn/10"
      : status === "offline"
      ? "bg-noc-err/10"
      : "bg-noc-muted";
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${color} flex-shrink-0`}
      title={status}
    />
  );
}
